import type { FieldPath, PathValue, UseFormReturn } from 'react-hook-form';
import type { AddressLookupWritableForm } from '../../../form-controls/Address/types';
import type { AddressLookupResult, FormValues } from '../types';
import { getRegistrationInternationalDialCode, processAddressFields, transformFormDataToApiPayload } from './payloadTransformer';

/**
 * Transforms the registration form data for address validation
 */
function transformAddressPayload(formData: FormValues): Record<string, unknown> {
  const basePayload = transformFormDataToApiPayload(formData);
  const addressFields = processAddressFields(formData.address || {});

  const payload = {
    ...basePayload,
    ...addressFields,
    limitAmount: 0,
    limitPeriod: 'DAY',
    mobilePhoneNumber: '',
    countryCode: getRegistrationInternationalDialCode(),
  };

  const finalStreetNr = addressFields.streetNr;
  if (finalStreetNr) {
    payload.streetNr = finalStreetNr;
  }

  return payload;
}

/**
 * Determines if an address is a PO Box
 */
function isPOBoxAddress(address: AddressLookupResult): boolean {
  const fields = [address.line1, address.line2, address.buildingName, address.street];
  return fields.some(field => field && (field.toLowerCase().includes('po box') || field.toLowerCase().includes('p.o. box') || field.toLowerCase().includes('post office box')));
}

/**
 * Determines if an address is an apartment
 */
function isApartmentAddress(address: AddressLookupResult): boolean {
  return !!(address.line1 && (address.line1.includes('Flat') || address.line1.includes('Apt')));
}

/**
 * Extracts PO Box number from address fields
 */
function extractPOBoxNumber(address: AddressLookupResult): string {
  const PO_BOX_REGEX = /(?:po\.?\s*box|p\.o\.?\s*box|post\s*office\s*box)\s*(\d+|[A-Za-z\d]+)/i;
  const fields = [address.line1, address.line2, address.buildingName, address.street];

  for (const field of fields) {
    if (field && PO_BOX_REGEX.test(field)) {
      const match = field.match(PO_BOX_REGEX);
      if (match && match[1]) {
        return match[1];
      }
    }
  }

  return address.line1 || address.buildingName || '';
}

/**
 * Extracts house name/number from address
 */
function extractHouseNameNumber(address: AddressLookupResult): string {
  if (address.line1 && address.line1.trim()) {
    if (address.line1.includes('Flat') || address.line1.includes('Apt') || address.line1.match(/^\d+[A-Za-z]?$/)) {
      return address.line1.trim();
    }
  }

  if (address.buildingNumber && address.buildingNumber.trim()) {
    return address.buildingNumber.trim();
  }

  if (address.buildingName && address.buildingName.trim()) {
    return address.buildingName.trim();
  }

  if (address.line1 && address.line1.trim()) {
    return address.line1.trim();
  }

  return '';
}

/**
 * Extracts street name from address
 */
function extractStreetName(address: AddressLookupResult, isApartment: boolean): string {
  if (address.street && address.street.trim()) {
    return address.street.trim();
  }

  if (isApartment && address.buildingName && address.buildingName.trim()) {
    const buildingName = address.buildingName.trim();
    if (!buildingName.match(/^(Flat|Apt|Unit|Suite)\s*\d+/i)) {
      return buildingName;
    }
  }

  return '';
}

/**
 * Extracts line2 from address
 */
function extractLine2(address: AddressLookupResult, isPOBox: boolean, isApartment: boolean): string {
  if (isPOBox) return '';

  const line2Value = address.line2 || '';

  if (isApartment) {
    if (line2Value && line2Value.trim() && !line2Value.includes(address.street || '') && line2Value !== address.buildingName) {
      return line2Value.trim();
    }
    return '';
  }

  if (line2Value && line2Value.trim() === (address.street || '').trim()) {
    return '';
  }

  return line2Value;
}

function applyAddressLookupResultToForm<T extends AddressLookupWritableForm>(form: Pick<UseFormReturn<T>, 'setValue'>, address: AddressLookupResult) {
  const setAddressField = (field: keyof AddressLookupWritableForm['address'], value: string) => {
    const name = `address.${field}` as FieldPath<T>;
    form.setValue(name, value as PathValue<T, typeof name>);
  };

  const isPOBox = isPOBoxAddress(address);
  const isApartment = isApartmentAddress(address);

  let houseNameNumber = '';
  let streetName = '';

  if (isPOBox) {
    houseNameNumber = extractPOBoxNumber(address);
    streetName = 'po box';
  } else {
    houseNameNumber = extractHouseNameNumber(address);
    streetName = extractStreetName(address, isApartment);
  }

  const line2 = extractLine2(address, isPOBox, isApartment);

  setAddressField('houseNameNumber', houseNameNumber);
  setAddressField('streetName', streetName);
  setAddressField('line2', line2);
  setAddressField('city', address.city || '');
  setAddressField('state', address.state || '');
  setAddressField('postcode', address.postcode || '');
  setAddressField('buildingName', address.buildingName || '');
  setAddressField('buildingNumber', address.buildingNumber || '');
  setAddressField('street', address.street || '');
}

export {
  applyAddressLookupResultToForm,
  extractHouseNameNumber,
  extractLine2,
  extractPOBoxNumber,
  extractStreetName,
  isApartmentAddress,
  isPOBoxAddress,
  transformAddressPayload,
  type AddressLookupWritableForm,
};
